import { useEffect, useRef, useState } from "react";

/**
 * useBlurCrossfade — returns `true` for one short window (~220ms) immediately
 * after `value` changes, so a value slot can play a blur-masked crossfade
 * (`filter: blur(2px)` + opacity) instead of hard-swapping.
 *
 * Pair with a CSS class that animates `filter`/`opacity` only (transform/opacity
 * are the cheap properties; blur is the masking trick from the design spec).
 *
 * Static by default — nothing animates until the value actually changes.
 */
export function useBlurCrossfade(
  value: string | number | null | undefined,
): boolean {
  const [changing, setChanging] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    if (prevRef.current !== value) {
      prevRef.current = value;
      setChanging(true);
      const t = setTimeout(() => setChanging(false), 220);
      return () => clearTimeout(t);
    }
  }, [value]);

  return changing;
}

export default useBlurCrossfade;
